import {
  useEffect,
  useMemo,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Bath,
  BrickWall,
  Brush,
  Check,
  ChefHat,
  ChevronRight,
  Clock,
  Construction,
  ExternalLink,
  FileText,
  HardHat,
  Info,
  Link as LinkIcon,
  MessageCircleQuestion,
  Package,
  PencilLine,
  Plus,
  Save,
  Shield,
  Sparkles,
  Trash2,
  Upload,
  WashingMachine,
  X,
  Zap,
  Megaphone,
} from "lucide-react";
import { api, ApiError, type Me, type Project } from "../api";
import { useAuth } from "../auth";
import { RiserImpactList, TelecomImpactHint } from "../components/CompliancePanels";
import { ScopeRefineButton } from "../components/ScopeRefine";
import { askElm } from "../components/elmChatBus";
import { Button, Field, cx } from "../ui";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type MyUnit = {
  id: string;
  unitNumber: string;
  floor: number | null;
  unitType: string | null;
  location: string | null;
  buildingId: string;
  buildingName: string;
  riserId: string;
  riserCode: string;
};

type ProjectType = {
  id: string;
  code: string;
  name: string;
  categoryCode: "A" | "B" | "C";
  scopeTemplate: string | null;
  description?: string | null;
};

type Cat = "A" | "B" | "C";

/** Human label shown to owners — A/B/C is insider language; Decorative
 *  / System-affecting / Structural is what makes sense to first-time
 *  owners reading the wizard. The letter is kept as a small chip so
 *  staff who think in codes still have an anchor. */
const CAT_LABEL: Record<Cat, string> = {
  A: "Decorative",
  B: "System-affecting",
  C: "Structural",
};
const CAT_LABEL_LONG: Record<Cat, string> = {
  A: "Decorative — paint, carpet, no Board approval",
  B: "System-affecting — kitchen, bath, washer/dryer, hard floors",
  C: "Structural — combining units or common-element work",
};

const TYPE_ICONS: Record<string, ReactNode> = {
  kitchen: <ChefHat size={24} />,
  bathroom: <Bath size={24} />,
  flooring_hard: <BrickWall size={24} />,
  washer_dryer: <WashingMachine size={24} />,
  electrical: <Zap size={24} />,
  paint: <Brush size={24} />,
  carpet_change: <Brush size={24} />,
  unit_combination: <Construction size={24} />,
  wall_modification: <Construction size={24} />,
};
const TYPE_ICON_FALLBACK = <Package size={24} />;

const STEPS = [
  { num: 1, label: "Type" },
  { num: 2, label: "Details" },
  { num: 3, label: "Impact" },
  { num: 4, label: "Materials" },
  { num: 5, label: "Contractors" },
  { num: 6, label: "Review" },
] as const;

type ImpactKey =
  | "requiresRiserShutdown"
  | "requiresTelecomShutdown"
  | "requiresCityPermit";

const IMPACTS: Array<{
  key: ImpactKey;
  title: string;
  desc: string;
  feeLabel: string;
  feeAmount?: number;
}> = [
  {
    key: "requiresRiserShutdown",
    title: "Requires water riser shutdown",
    desc: "Work involving supply lines, valves, or the building's water riser. 72 hr advance notice required.",
    feeLabel: "+$150",
    feeAmount: 150,
  },
  {
    key: "requiresTelecomShutdown",
    title: "Telecom / cable / antenna shutdown",
    desc: "First per system free; $100 each subsequent. 10 AM – 3 PM Mon–Fri only.",
    feeLabel: "First free",
  },
  {
    key: "requiresCityPermit",
    title: "Requires City of Chicago permit",
    desc: "Tick this when the scope crosses Department of Buildings review (structural changes, electrical service upgrades, plumbing relocation, etc.). Most cosmetic refreshes don't.",
    feeLabel: "City fees",
  },
];

const CONTRACTOR_DISCIPLINES = [
  { value: "general", label: "General Contractor" },
  { value: "plumbing", label: "Plumbing" },
  { value: "electrical", label: "Electrical" },
  { value: "hvac", label: "HVAC" },
] as const;

type ContractorDraft = {
  id: string; // local-only id for keys
  discipline: (typeof CONTRACTOR_DISCIPLINES)[number]["value"];
  companyName: string;
  contactName: string;
  email: string;
  phone: string;
};

const DOC_TYPES_FOR_UPLOAD = [
  { code: "scope_of_work", label: "Scope of work narrative / drawings" },
  { code: "architectural_drawings", label: "Architectural drawings" },
  { code: "appliance_spec_washer_dryer", label: "Appliance spec (washer / dryer)" },
  { code: "flooring_underlayment_spec", label: "Flooring / underlayment spec" },
  { code: "before_photos", label: "Before photos" },
  { code: "city_permit", label: "City of Chicago permit" },
] as const;

type FileUpload = {
  id: string;
  file: File;
  documentTypeCode: string;
};

type MaterialLink = { url: string; label: string };
type FlooringDetails = {
  floorType: string;
  floorTypeLink: string;
  underlayment: string;
  underlaymentLink: string;
  removalRequired: boolean;
  currentFlooringDescription: string;
};
const EMPTY_FLOORING_DETAILS: FlooringDetails = {
  floorType: "",
  floorTypeLink: "",
  underlayment: "",
  underlaymentLink: "",
  removalRequired: false,
  currentFlooringDescription: "",
};
const HARD_SURFACE_FLOORING_CODE = "flooring_hard";

// ---------------------------------------------------------------------------
// Per-project-type guidance + structured extra fields
//
// TYPE_GUIDANCE drives helper text (placeholders, scope checklists,
// suggested material links, impact notes, contractor notes) so each
// step's copy adapts to whatever the owner picked.
//
// TYPE_EXTRA_FIELDS describes structured fields owners need to fill
// in for specific types (e.g. cabinet brand for kitchen, washer
// model for washer/dryer). Values land on metadata.typeDetails so
// Management can review them without parsing the free-form scope.
// ---------------------------------------------------------------------------
type TypeGuidance = {
  titlePlaceholder: string;
  scopeChecklist: string[];
  materialSuggestions: string[];
  impactNotes: string[];
  contractorNote: string;
};
const TYPE_GUIDANCE: Record<string, TypeGuidance> = {
  paint: {
    titlePlaceholder: "e.g. Living + bedroom repaint",
    scopeChecklist: [
      "Rooms affected",
      "Confirm decorative-only — no plumbing, electrical, or structural changes",
    ],
    materialSuggestions: ["Paint manufacturer color page", "Wallpaper SKU"],
    impactNotes: [
      "Decorative work — typically no riser or telecom shutdown needed.",
    ],
    contractorNote:
      "No licensed contractor is required for paint. You can still list a painter for the staff record.",
  },
  carpet_change: {
    titlePlaceholder: "e.g. Bedroom carpet replacement",
    scopeChecklist: [
      "Rooms affected",
      "Confirm no underlayment changes that affect building acoustics",
    ],
    materialSuggestions: ["Carpet manufacturer SKU", "Pad spec"],
    impactNotes: [
      "Decorative work — no riser or telecom shutdown expected.",
    ],
    contractorNote:
      "No licensed contractor is required. Installer name is fine for the record.",
  },
  kitchen: {
    titlePlaceholder: "e.g. Kitchen renovation",
    scopeChecklist: [
      "Plumbing fixtures (sink, dishwasher hookup, shut-off valves, dielectric unions)",
      "New electrical outlets / GFCI / lighting",
      "Backsplash tile",
      "Confirm no relocation of risers, waste lines, or vent stacks",
      "Architectural drawings + City of Chicago permit attached",
    ],
    materialSuggestions: [
      "Cabinet maker (e.g., IKEA SEKTION, Semihandmade)",
      "Countertop slab spec",
      "Sink + faucet",
      "Dishwasher model",
      "Range / hood",
      "Backsplash tile",
    ],
    impactNotes: [
      "New shut-off valves typically need a water riser shutdown.",
    ],
    contractorNote:
      "Required: General Contractor, City of Chicago licensed plumber, City of Chicago licensed electrician.",
  },
  bathroom: {
    titlePlaceholder: "e.g. Master bath renovation",
    scopeChecklist: [
      "Tub / shower surround spec",
      "Plumbing fixtures (toilet, sink, shower)",
      "Tile flooring with required acoustical underlayment per Exhibit B",
      "GFCI electrical",
      "Confirm no relocation of risers, waste lines, or vent stacks",
      "Architectural drawings attached where applicable",
    ],
    materialSuggestions: [
      "Tub / shower",
      "Toilet",
      "Sink + faucet",
      "Floor tile",
      "Wall tile",
      "Vanity",
    ],
    impactNotes: [
      "Replacing fixtures typically needs a water riser shutdown.",
    ],
    contractorNote:
      "Required: General Contractor and a City of Chicago licensed plumber. Licensed electrician if outlets/lighting change.",
  },
  flooring_hard: {
    titlePlaceholder: "e.g. Living + dining hardwood install",
    scopeChecklist: [
      "Floor type (wood / wood-laminate / ceramic / stone)",
      "Underlayment per Exhibit A (wood) or B (tile)",
      "Latex leveling within Rule limits (3/4\" max for light finish; 1/2\" max for heavy stone)",
      "Three-phase Chief Engineer inspection acknowledged",
    ],
    materialSuggestions: [
      "Floor product (Floor & Decor, Lumber Liquidators, etc.)",
      "Underlayment / cork product",
    ],
    impactNotes: [
      "Hard-surface flooring rarely needs a water riser shutdown unless tile work touches plumbing.",
    ],
    contractorNote:
      "Required: General Contractor. Floor installer can sit under the GC.",
  },
  washer_dryer: {
    titlePlaceholder: "e.g. In-unit washer/dryer install",
    scopeChecklist: [
      "Location near the kitchen waste riser only",
      "Washer make + model",
      "Dryer make + model — ventless electric only (vented dryers prohibited)",
      "Leak detection regulator with auto shut-off",
      "2\" code-approved air gap on drain",
      "Vacuum breakers + dielectric unions",
      "Fabcel 200 (or equal) machinery mounting pads",
    ],
    materialSuggestions: [
      "Washer make + model spec sheet",
      "Dryer make + model spec sheet",
      "Leak-detection unit (e.g., FloodMaster RS-094)",
      "Mounting pads",
    ],
    impactNotes: [
      "Hookup typically needs a water riser shutdown.",
    ],
    contractorNote:
      "Required: General Contractor, licensed plumber, licensed electrician.",
  },
  wall_modification: {
    titlePlaceholder: "e.g. Open kitchen — remove dining partition",
    scopeChecklist: [
      "Specific walls being removed or relocated",
      "Confirm no load-bearing walls or shear walls affected",
      "Confirm no channeling of support columns",
      "Floor-slab channeling ≤1\" depth or rebar depth, whichever is less",
      "Demising walls modified to STC ≥ 54 where required",
      "Architectural drawings sealed by a licensed Architect (where required)",
      "Pre-wall-close inspection by Architectural Representative requested",
    ],
    materialSuggestions: [
      "Architect's drawing set",
      "Insulation / acoustic spec sheet for demising walls",
    ],
    impactNotes: [
      "Wall modifications rarely affect plumbing risers. Confirm before checking riser shutdown.",
    ],
    contractorNote: "Required: General Contractor.",
  },
  unit_combination: {
    titlePlaceholder: "e.g. Combine 1108 + 1109",
    scopeChecklist: [
      "Units being combined",
      "Architectural drawings sealed by a licensed Architect",
      "License Agreement with the Association signed",
      "Structural review by Building Engineer requested",
    ],
    materialSuggestions: ["Architect's drawing set"],
    impactNotes: [
      "Cross-unit work almost always needs a riser shutdown for plumbing reroutes.",
    ],
    contractorNote:
      "Required: General Contractor, licensed plumber, licensed electrician. Engineer review per the Association.",
  },
};

// Structured extra fields per project type. Values are persisted on
// metadata.typeDetails[code]. Optional — the wizard renders only what
// the owner has selected.
//
// `showWhen` makes a field conditional on another field's value in
// the same panel. We render the field only when the referenced field
// currently equals the target. The combine flag itself is a regular
// checkbox; conditional sub-fields point back at its key.
type ExtraField =
  | {
      kind: "text";
      key: string;
      label: string;
      placeholder?: string;
      hint?: string;
      showWhen?: { key: string; equals: string | boolean };
    }
  | {
      kind: "url";
      key: string;
      label: string;
      placeholder?: string;
      hint?: string;
      showWhen?: { key: string; equals: string | boolean };
    }
  | {
      kind: "textarea";
      key: string;
      label: string;
      placeholder?: string;
      hint?: string;
      showWhen?: { key: string; equals: string | boolean };
    }
  | {
      kind: "checkbox";
      key: string;
      label: string;
      hint?: string;
      showWhen?: { key: string; equals: string | boolean };
    }
  | {
      kind: "file";
      /** Stable key so we can show "attached" state across re-renders. */
      key: string;
      label: string;
      /** Document type code the file gets assigned in pendingFiles. */
      documentTypeCode: string;
      hint?: string;
      showWhen?: { key: string; equals: string | boolean };
    }
  | {
      // Static informational banner. Rendered as a small notice;
      // not persisted. Use to remind the owner about a downstream
      // implication of a flag (e.g. "Plumbing is required when
      // cabinets are being replaced").
      kind: "notice";
      key: string;
      label: string;
      hint?: string;
      showWhen?: { key: string; equals: string | boolean };
    };

const TYPE_EXTRA_FIELDS: Record<string, ExtraField[]> = {
  kitchen: [
    {
      kind: "text",
      key: "dishwasherModel",
      label: "Dishwasher make + model",
      placeholder: "Bosch SHEM63W55N",
    },
    {
      kind: "url",
      key: "specsLink",
      label: "Specifications link",
      placeholder: "https://…",
      hint: "Cabinet maker, countertop slab, appliance spec page — anywhere Management can review the materials.",
    },
    {
      kind: "file",
      key: "specsFile",
      label: "…or attach a spec sheet",
      documentTypeCode: "architectural_drawings",
      hint: "PDF or image. Files attached here get tagged as architectural drawings on Step 4.",
    },
    {
      kind: "checkbox",
      key: "replacingCabinets",
      label: "Replacing kitchen cabinets",
      hint: "Triggers required details for the new countertop, faucet, and sink below. Plumbing scope and a licensed plumber are required.",
    },
    {
      kind: "notice",
      key: "cabinetsNotice",
      label:
        "Plumbing scope is required: shut-off valves and dielectric unions on all new fixtures, all work by a City of Chicago licensed plumber (plumber's COI and license must be on file).",
      showWhen: { key: "replacingCabinets", equals: true },
    },
    {
      kind: "text",
      key: "countertopDescription",
      label: "Countertop — material / brand",
      placeholder: 'e.g. Quartz, Caesarstone "Calacatta Nuvo" 3cm',
      hint: "Required when replacing cabinets.",
      showWhen: { key: "replacingCabinets", equals: true },
    },
    {
      kind: "url",
      key: "countertopLink",
      label: "Countertop — link (optional)",
      placeholder: "https://…",
      showWhen: { key: "replacingCabinets", equals: true },
    },
    {
      kind: "file",
      key: "countertopFile",
      label: "Countertop — attach spec sheet (optional)",
      documentTypeCode: "architectural_drawings",
      showWhen: { key: "replacingCabinets", equals: true },
    },
    {
      kind: "text",
      key: "faucetDescription",
      label: "Faucet — make / model",
      placeholder: "e.g. Delta Trinsic 9159-AR-DST",
      hint: "Required when replacing cabinets.",
      showWhen: { key: "replacingCabinets", equals: true },
    },
    {
      kind: "url",
      key: "faucetLink",
      label: "Faucet — link (optional)",
      placeholder: "https://…",
      showWhen: { key: "replacingCabinets", equals: true },
    },
    {
      kind: "file",
      key: "faucetFile",
      label: "Faucet — attach spec sheet (optional)",
      documentTypeCode: "architectural_drawings",
      showWhen: { key: "replacingCabinets", equals: true },
    },
    {
      kind: "text",
      key: "sinkDescription",
      label: "Sink — make / model",
      placeholder: 'e.g. Kraus KHU100-32, 32" stainless undermount',
      hint: "Required when replacing cabinets.",
      showWhen: { key: "replacingCabinets", equals: true },
    },
    {
      kind: "url",
      key: "sinkLink",
      label: "Sink — link (optional)",
      placeholder: "https://…",
      showWhen: { key: "replacingCabinets", equals: true },
    },
    {
      kind: "file",
      key: "sinkFile",
      label: "Sink — attach spec sheet (optional)",
      documentTypeCode: "architectural_drawings",
      showWhen: { key: "replacingCabinets", equals: true },
    },
  ],
  bathroom: [
    {
      kind: "text",
      key: "tubShower",
      label: "Tub or shower surround",
      placeholder: "American Standard alcove tub + ceramic surround",
    },
    {
      kind: "text",
      key: "tileType",
      label: "Floor + wall tile",
      placeholder: "12x24 porcelain, matte",
    },
    {
      kind: "text",
      key: "vanity",
      label: "Vanity manufacturer + size",
      placeholder: 'Custom 36" vanity with quartz top',
    },
  ],
  washer_dryer: [
    {
      kind: "checkbox",
      key: "existingWasher",
      label: "There is currently a washer / dryer installed in this unit",
      hint: "Tells Management whether this is a replacement or a brand-new install.",
    },
    {
      kind: "text",
      key: "washerModel",
      label: "Washer make + model",
      placeholder: "LG WM4000HWA",
    },
    {
      kind: "text",
      key: "dryerModel",
      label: "Dryer make + model",
      placeholder: "LG DLEX4000W heat-pump",
      hint: "Ventless electric only — vented dryers are prohibited per the Rules.",
    },
    {
      kind: "text",
      key: "leakSensor",
      label: "Leak-detection unit",
      placeholder: "FloodMaster RS-094",
    },
    {
      kind: "checkbox",
      key: "ventlessConfirmed",
      label:
        "I confirm the dryer is ventless electric (vented dryers are prohibited)",
    },
    {
      kind: "url",
      key: "specsLink",
      label: "Specifications link",
      placeholder: "https://…",
      hint: "Manufacturer spec page for the washer, dryer, or leak sensor.",
    },
    {
      kind: "file",
      key: "specsFile",
      label: "…or attach a spec sheet",
      documentTypeCode: "appliance_spec_washer_dryer",
      hint: "PDF or image. Tagged as Washer/Dryer Appliance Specifications on Step 4.",
    },
  ],
  wall_modification: [
    {
      kind: "textarea",
      key: "wallsModified",
      label: "Which walls are being removed or relocated",
      placeholder: "Removing the partition between the kitchen and the dining area",
    },
    {
      kind: "checkbox",
      key: "noLoadBearing",
      label:
        "I confirm no load-bearing walls or shear walls are affected, and no support columns are being channeled",
    },
    {
      kind: "checkbox",
      key: "demisingSTC",
      label:
        "Any demising walls between units modified to STC ≥ 54 (or N/A — no demising walls touched)",
    },
  ],
  unit_combination: [
    {
      kind: "text",
      key: "unitsCombined",
      label: "Units being combined",
      placeholder: "1108 + 1109",
    },
    {
      kind: "checkbox",
      key: "architectSealed",
      label:
        "Architectural drawings will be sealed by a licensed Architect",
    },
  ],
};

type TypeDetails = Record<string, Record<string, string | boolean>>;

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export function NewProjectPage() {
  const { state } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();

  const [step, setStep] = useState(1);

  // Loaded data
  const [units, setUnits] = useState<MyUnit[]>([]);
  const [unitsLoading, setUnitsLoading] = useState(true);
  const [types, setTypes] = useState<ProjectType[]>([]);
  const [typesLoading, setTypesLoading] = useState(true);

  // Selections / inputs
  const [unitId, setUnitId] = useState("");
  /** Multiple types are allowed — owners often combine work (kitchen +
   * bathroom + flooring). The first id in this array is the primary
   * (drives Category). The server re-orders by category to make sure
   * the most-restrictive type is treated as primary. */
  const [selectedTypeIds, setSelectedTypeIds] = useState<string[]>([]);
  const [title, setTitle] = useState("");
  const [scopeOfWork, setScopeOfWork] = useState("");
  const [scopeTouched, setScopeTouched] = useState(false);
  const [requiresRiserShutdown, setRiser] = useState(false);
  const [requiresTelecomShutdown, setTelecom] = useState(false);
  const [requiresCityPermit, setCityPermit] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<FileUpload[]>([]);
  const [materialLinks, setMaterialLinks] = useState<MaterialLink[]>([]);
  const [flooringDetails, setFlooringDetails] = useState<FlooringDetails>(
    EMPTY_FLOORING_DETAILS,
  );
  const [typeDetails, setTypeDetails] = useState<TypeDetails>({});
  const [contractors, setContractors] = useState<ContractorDraft[]>([]);

  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // White-glove submission: PMs and admins land here with
  // ?onBehalfOfUserId=… so the wizard can fetch the target
  // owner's units instead of the principal's. Everything else
  // flows the same; the POST /projects body forwards
  // onBehalfOfUserId so the server records the relationship.
  const onBehalfOfUserId = params.get("onBehalfOfUserId") ?? null;

  // Load units. Owner lane uses /api/my/units; white-glove uses
  // /api/management/users/:userId/units, which is PM/admin-only
  // and scoped server-side to the principal's buildings.
  useEffect(() => {
    const url = onBehalfOfUserId
      ? `/api/management/users/${onBehalfOfUserId}/units`
      : "/api/my/units";
    void api
      .get<{ data: MyUnit[] }>(url)
      .then((r) => {
        setUnits(r.data);
        // Prefer ?unitId=… if the caller pre-selected a unit
        // (the admin user-edit panel does this so the staff
        // member doesn't have to pick again). Otherwise default
        // to the first unit in the list.
        const preselected = params.get("unitId") ?? "";
        if (preselected && r.data.some((u) => u.id === preselected)) {
          setUnitId(preselected);
        } else if (r.data[0]) {
          setUnitId(r.data[0].id);
        }
      })
      .catch(() => setUnits([]))
      .finally(() => setUnitsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onBehalfOfUserId]);

  // Load project types as soon as we know which building to query.
  // Falls back to /api/buildings (any role) when the user owns no
  // units — that way an admin or invited owner-without-unit can still
  // see the types and the wizard's empty state stays informative.
  const selectedUnit = useMemo(
    () => units.find((u) => u.id === unitId) ?? null,
    [units, unitId],
  );
  const [fallbackBuildingId, setFallbackBuildingId] = useState<string | null>(
    null,
  );
  useEffect(() => {
    if (!unitsLoading && units.length === 0) {
      void api
        .get<{ data: { id: string }[] }>("/api/buildings")
        .then((r) => {
          if (r.data[0]) setFallbackBuildingId(r.data[0].id);
        })
        .catch(() => {});
    }
  }, [unitsLoading, units.length]);

  const buildingForTypes =
    selectedUnit?.buildingId ?? fallbackBuildingId ?? null;
  useEffect(() => {
    if (!buildingForTypes) return;
    setTypesLoading(true);
    void api
      .get<{ data: ProjectType[] }>(
        `/api/buildings/${buildingForTypes}/project-types`,
      )
      .then((r) => {
        setTypes(r.data);
        const wanted = params.get("type");
        if (wanted && selectedTypeIds.length === 0) {
          const found = r.data.find((t) => t.code === wanted);
          if (found) setSelectedTypeIds([found.id]);
        }
      })
      .catch(() => setTypes([]))
      .finally(() => setTypesLoading(false));
    // re-running when selectedTypeIds changes is intentional only the
    // first time (auto-pick from ?type=); we guard with length===0.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingForTypes]);

  const primaryTypeId = selectedTypeIds[0] ?? "";
  const selectedTypes = useMemo(
    () =>
      selectedTypeIds
        .map((id) => types.find((t) => t.id === id))
        .filter((t): t is ProjectType => !!t),
    [types, selectedTypeIds],
  );
  const primaryType = selectedTypes[0] ?? null;
  const hasHardSurfaceFlooring = selectedTypes.some(
    (t) => t.code === HARD_SURFACE_FLOORING_CODE,
  );

  // Aggregate guidance across all selected types. Use the union of
  // checklists / suggestions / contractor notes — the owner sees
  // every relevant item from every type they picked, deduped.
  const aggregateGuidance = useMemo<TypeGuidance | null>(() => {
    const codes = selectedTypes.map((t) => t.code);
    const guides = codes
      .map((c) => TYPE_GUIDANCE[c])
      .filter((g): g is TypeGuidance => !!g);
    if (guides.length === 0) return null;
    const dedupe = (xs: string[]) => Array.from(new Set(xs));
    return {
      titlePlaceholder: guides[0]!.titlePlaceholder,
      scopeChecklist: dedupe(guides.flatMap((g) => g.scopeChecklist)),
      materialSuggestions: dedupe(guides.flatMap((g) => g.materialSuggestions)),
      impactNotes: dedupe(guides.flatMap((g) => g.impactNotes)),
      contractorNote: guides
        .map((g) => g.contractorNote)
        .filter((s, i, a) => a.indexOf(s) === i)
        .join(" "),
    };
  }, [selectedTypes]);

  const setTypeFieldValue = (
    code: string,
    key: string,
    value: string | boolean,
  ) =>
    setTypeDetails((prev) => ({
      ...prev,
      [code]: { ...(prev[code] ?? {}), [key]: value },
    }));

  // Auto-fill the scope template the first time a type is picked, but
  // never overwrite text the owner has already started typing.
  useEffect(() => {
    if (!primaryType) return;
    if (scopeTouched) return;
    if (primaryType.scopeTemplate) {
      const populated = primaryType.scopeTemplate.replaceAll(
        "[UNIT]",
        selectedUnit?.unitNumber ?? "[UNIT]",
      );
      setScopeOfWork(populated);
    }
  }, [primaryType?.id, selectedUnit?.unitNumber, scopeTouched]);

  const feeTotal = (requiresRiserShutdown ? 150 : 0);

  // Validation per step. Returns null when the step is OK, or a
  // user-facing reason string when it isn't. The reason gets surfaced
  // in the sticky footer next to the disabled Continue button so the
  // owner knows what's blocking them rather than guessing.
  const stepBlocker = ((): string | null => {
    if (step === 1) {
      if (selectedTypeIds.length === 0) {
        return "Pick at least one project type to continue.";
      }
      return null;
    }
    if (step === 2) {
      if (!unitId) return "Select the unit this project is for.";
      if (title.trim().length < 3) {
        return "Title needs at least 3 characters.";
      }
      if (scopeOfWork.trim().length < 10) {
        return "Scope of work needs at least 10 characters — name the rooms, fixtures, and materials.";
      }
      // Type-specific safety confirmations: these are commitments
      // about the work, not nice-to-haves. Don't let the owner past
      // step 2 without ticking them.
      const codes = new Set(selectedTypes.map((t) => t.code));
      if (codes.has("washer_dryer")) {
        const ack = typeDetails["washer_dryer"]?.["ventlessConfirmed"];
        if (ack !== true) {
          return "Confirm the dryer is ventless electric (vented dryers are prohibited).";
        }
      }
      if (codes.has("wall_modification")) {
        const ack = typeDetails["wall_modification"]?.["noLoadBearing"];
        if (ack !== true) {
          return "Confirm no load-bearing walls or shear walls are affected.";
        }
      }
      return null;
    }
    if (step === 5) {
      // Don't require any specific contractor — they can be invited
      // from the project page later. Just refuse half-filled rows so
      // we don't end up with a contractor name and no way to email
      // them, or vice versa.
      for (const c of contractors) {
        const name = c.companyName.trim();
        const email = c.email.trim();
        if (name && !email) {
          return `Add an email for ${name}, or remove the row.`;
        }
        if (!name && email) {
          return `Add a company name for the contractor at ${email}, or remove the row.`;
        }
      }
      return null;
    }
    return null;
  })();
  const canContinue = stepBlocker === null;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const cleanedFlooring = hasHardSurfaceFlooring
        ? {
            floorType: flooringDetails.floorType.trim(),
            floorTypeLink: flooringDetails.floorTypeLink.trim(),
            underlayment: flooringDetails.underlayment.trim(),
            underlaymentLink: flooringDetails.underlaymentLink.trim(),
            removalRequired: flooringDetails.removalRequired,
            currentFlooringDescription: flooringDetails.removalRequired
              ? flooringDetails.currentFlooringDescription.trim()
              : "",
          }
        : null;
      const flooringPayload =
        cleanedFlooring &&
        (cleanedFlooring.floorType ||
          cleanedFlooring.floorTypeLink ||
          cleanedFlooring.underlayment ||
          cleanedFlooring.underlaymentLink ||
          cleanedFlooring.removalRequired)
          ? cleanedFlooring
          : undefined;

      // Drop empty type-detail blobs so we don't store noise.
      const cleanedTypeDetails: TypeDetails = {};
      for (const code of Object.keys(typeDetails)) {
        const fields = typeDetails[code] ?? {};
        const trimmed: Record<string, string | boolean> = {};
        for (const [k, v] of Object.entries(fields)) {
          if (typeof v === "string") {
            const t = v.trim();
            if (t) trimmed[k] = t;
          } else if (v === true) {
            trimmed[k] = true;
          }
        }
        if (Object.keys(trimmed).length > 0) cleanedTypeDetails[code] = trimmed;
      }
      const typeDetailsPayload =
        Object.keys(cleanedTypeDetails).length > 0
          ? cleanedTypeDetails
          : undefined;

      const created = await api.post<{ data: Project }>("/api/projects", {
        unitId,
        projectTypeId: primaryTypeId,
        additionalProjectTypeIds: selectedTypeIds.slice(1),
        title,
        scopeOfWork,
        requiresRiserShutdown,
        requiresTelecomShutdown,
        requiresCityPermit,
        materialLinks: materialLinks
          .filter((m) => m.url.trim())
          .map((m) => ({
            url: m.url.trim(),
            ...(m.label.trim() ? { label: m.label.trim() } : {}),
          })),
        ...(flooringPayload ? { flooringDetails: flooringPayload } : {}),
        ...(typeDetailsPayload ? { typeDetails: typeDetailsPayload } : {}),
        // White-glove: forward the target owner so the server
        // records submitted_on_behalf_of_user_id. The principal
        // (the staff member) stays the submitted_by attribution.
        ...(onBehalfOfUserId ? { onBehalfOfUserId } : {}),
      });
      const projectId = created.data.id;

      // Best-effort upload of any queued documents.
      for (const f of pendingFiles) {
        const fd = new FormData();
        fd.append("file", f.file);
        fd.append("documentTypeCode", f.documentTypeCode);
        try {
          await fetch(`/api/projects/${projectId}/documents`, {
            method: "POST",
            credentials: "include",
            body: fd,
          });
        } catch {
          // Surface as a per-file warning later; don't fail the whole flow.
        }
      }

      // Best-effort contractor invites.
      for (const c of contractors) {
        if (!c.companyName.trim() || !c.email.trim()) continue;
        try {
          await api.post(`/api/projects/${projectId}/contractors`, {
            role: c.discipline,
            companyName: c.companyName.trim(),
            companyDiscipline: c.discipline,
            contactName: c.contactName.trim() || undefined,
            primaryContactEmail: c.email.trim(),
            phone: c.phone.trim() || undefined,
          });
        } catch {
          // Non-fatal — owner can re-invite from the project page.
        }
      }

      nav(`/projects/${projectId}`, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        const code =
          (typeof err.body === "object" &&
            err.body &&
            "error" in err.body &&
            (err.body as { error?: string }).error) ||
          "create_failed";
        setError(
          code === "not_unit_owner"
            ? "You aren't recorded as an owner of that unit."
            : code === "riser_at_capacity"
              ? "This unit's tier group is at washer/dryer hookup capacity."
              : code === "unit_already_has_wd"
                ? "This unit already has a washer/dryer hookup."
                : `Create failed: ${code}`,
        );
      } else setError("Create failed.");
    } finally {
      setBusy(false);
    }
  };

  if (state.status !== "authenticated") return null;

  // Display name for the white-glove banner. The first owned unit
  // carries no owner-name field, so we'll show "Unit X at <building>"
  // until the staff member confirms a unit.
  const whiteGloveLabel = onBehalfOfUserId
    ? selectedUnit
      ? `Unit ${selectedUnit.unitNumber} at ${selectedUnit.buildingName}`
      : "the selected owner"
    : null;

  return (
    <div className="-mt-6 -mx-4 sm:-mx-6 lg:-mx-8">
      {whiteGloveLabel && (
        <div className="bg-status-review-bg text-status-review-fg border-b border-status-review-fg/20 px-4 sm:px-6 lg:px-10 py-2.5">
          <div className="max-w-6xl mx-auto text-[12.5px] inline-flex items-center gap-2 flex-wrap">
            <strong>White-glove submission</strong>
            <span>
              You're creating this draft on behalf of {whiteGloveLabel}. The
              owner will receive the draft on their account; submission
              checks (insurance, indemnity) still run against their record.
            </span>
          </div>
        </div>
      )}
      {/* Wizard header */}
      <div className="bg-brand-800 text-white border-b border-white/[0.07]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-5 flex items-center gap-5 flex-wrap">
          <Link
            to="/projects"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/[0.09] border border-white/[0.12] rounded-sm text-[12.5px] text-white/80 hover:bg-white/[0.15] hover:text-white no-underline hover:no-underline transition-colors"
          >
            <ArrowLeft size={13} /> All Projects
          </Link>
          <div className="flex-1 min-w-[200px]">
            <div className="text-[10px] tracking-[0.1em] uppercase font-medium text-brand-400 mb-0.5">
              New Project
            </div>
            <div className="font-display text-[22px] font-medium text-white tracking-[-0.01em]">
              {onBehalfOfUserId
                ? "Set up the project for this owner"
                : "Let's set up your project"}
            </div>
          </div>
          <Steps step={step} onJumpTo={(n) => setStep(n)} />
        </div>
      </div>

      {/* Body */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_316px] min-h-[60vh]">
        <div className="px-4 sm:px-6 lg:px-10 py-9 bg-sand-50 border-b lg:border-b-0 lg:border-r border-sand-100">
          {/*
            Mobile-only inline disclosure for the contextual sidebar
            so the hints don't disappear below the main content on
            phones. The aside on the right keeps showing on lg+ via
            its own block at the bottom of the grid.
          */}
          <details className="lg:hidden mb-4 bg-white border border-sand-200 rounded-md">
            <summary className="cursor-pointer px-3 py-2 text-[12.5px] font-medium text-ink-700 inline-flex items-center gap-1.5">
              <Sparkles size={13} className="text-brand-700" />
              Hints &amp; reminders for this step
            </summary>
            <div className="px-3 py-3 border-t border-sand-100">
              {step === 1 && <SidebarCategories />}
              {step === 2 && <SidebarSelection type={primaryType} />}
              {step === 3 && (
                <SidebarFees
                  feeTotal={feeTotal}
                  requiresRiserShutdown={requiresRiserShutdown}
                />
              )}
              {step === 4 && <SidebarMaterials />}
              {step === 5 && <SidebarContractors />}
              {step === 6 && <SidebarAfterSave />}
            </div>
          </details>
          {step === 1 && (
            <StepType
              types={types}
              loading={typesLoading}
              ownsAnyUnit={units.length > 0}
              selectedTypeIds={selectedTypeIds}
              onToggle={(id) =>
                setSelectedTypeIds((curr) =>
                  curr.includes(id)
                    ? curr.filter((x) => x !== id)
                    : [...curr, id],
                )
              }
              onClear={() => setSelectedTypeIds([])}
              me={state.status === "authenticated" ? state.me : null}
              onBehalfOfUserId={onBehalfOfUserId}
              onPickWhiteGlove={(target) => {
                // Writing the search params re-runs the units
                // load effect, which swaps from /api/my/units to
                // /api/management/users/:userId/units. The banner
                // appears once those units come back.
                setParams(
                  {
                    onBehalfOfUserId: target.userId,
                    unitId: target.unitId,
                  },
                  { replace: true },
                );
              }}
              onClearWhiteGlove={() => {
                const next = new URLSearchParams(params);
                next.delete("onBehalfOfUserId");
                next.delete("unitId");
                setParams(next, { replace: true });
              }}
            />
          )}
          {step === 2 && (
            <StepDetails
              units={units}
              unitsLoading={unitsLoading}
              unitId={unitId}
              setUnitId={setUnitId}
              title={title}
              setTitle={setTitle}
              scopeOfWork={scopeOfWork}
              setScopeOfWork={(v) => {
                setScopeOfWork(v);
                setScopeTouched(true);
              }}
              selectedType={primaryType}
              selectedUnit={selectedUnit}
              selectedTypes={selectedTypes}
              guidance={aggregateGuidance}
              typeDetails={typeDetails}
              setTypeFieldValue={setTypeFieldValue}
              pendingFiles={pendingFiles}
              setPendingFiles={setPendingFiles}
              hasHardSurfaceFlooring={hasHardSurfaceFlooring}
              flooringDetails={flooringDetails}
              setFlooringDetails={setFlooringDetails}
            />
          )}
          {step === 3 && (
            <StepImpact
              requiresRiserShutdown={requiresRiserShutdown}
              setRiser={setRiser}
              requiresTelecomShutdown={requiresTelecomShutdown}
              setTelecom={setTelecom}
              requiresCityPermit={requiresCityPermit}
              setCityPermit={setCityPermit}
              guidance={aggregateGuidance}
              selectedUnit={selectedUnit}
            />
          )}
          {step === 4 && (
            <StepMaterials
              pendingFiles={pendingFiles}
              setPendingFiles={setPendingFiles}
              materialLinks={materialLinks}
              setMaterialLinks={setMaterialLinks}
              guidance={aggregateGuidance}
            />
          )}
          {step === 5 && (
            <StepContractors
              contractors={contractors}
              setContractors={setContractors}
              selectedType={primaryType}
              guidance={aggregateGuidance}
            />
          )}
          {step === 6 && (
            <StepReview
              types={selectedTypes}
              unit={selectedUnit}
              title={title}
              scopeOfWork={scopeOfWork}
              requiresRiserShutdown={requiresRiserShutdown}
              requiresTelecomShutdown={requiresTelecomShutdown}
              pendingFiles={pendingFiles}
              materialLinks={materialLinks}
              contractors={contractors}
            />
          )}
        </div>
        <aside className="hidden lg:block px-5 sm:px-6 py-7 bg-white">
          {step === 1 && <SidebarCategories />}
          {step === 2 && <SidebarSelection type={primaryType} />}
          {step === 3 && (
            <SidebarFees
              feeTotal={feeTotal}
              requiresRiserShutdown={requiresRiserShutdown}
            />
          )}
          {step === 4 && <SidebarMaterials />}
          {step === 5 && <SidebarContractors />}
          {step === 6 && <SidebarAfterSave />}
        </aside>
      </div>

      {/* Sticky footer */}
      <div className="sticky bottom-0 z-10 bg-white border-t border-sand-100 px-4 sm:px-6 lg:px-10 py-3.5 flex items-center justify-between flex-wrap gap-3">
        <div className="text-[12px] text-ink-400 min-w-0 flex items-baseline gap-2 flex-wrap">
          <span>
            Step <strong className="text-ink-600 font-medium">{step}</strong> of{" "}
            {STEPS.length}
          </span>
          {selectedTypes.length > 0 && (
            <span
              className="text-ink-600 truncate max-w-[60vw] sm:max-w-[40vw]"
              title={selectedTypes.map((t) => t.name).join(", ")}
            >
              · {selectedTypes.map((t) => t.name).join(", ")}
            </span>
          )}
        </div>
        {(error || (stepBlocker && step < STEPS.length)) && (
          <div className="hidden sm:block flex-1 mx-4 text-right text-[12px] text-status-rejected-fg">
            {error ?? stepBlocker}
          </div>
        )}
        <div className="flex gap-2">
          {step > 1 && (
            <Button variant="ghost" onClick={() => setStep((s) => s - 1)}>
              <ArrowLeft size={14} /> Back
            </Button>
          )}
          {step < STEPS.length ? (
            <Button
              variant="primary"
              disabled={!canContinue}
              onClick={() => setStep((s) => s + 1)}
            >
              Continue <ArrowRight size={14} />
            </Button>
          ) : (
            <Button
              variant="success"
              onClick={() => void submit()}
              loading={busy}
            >
              <Check size={14} /> Save draft
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Steps progress indicator
// ---------------------------------------------------------------------------
function Steps({
  step,
  onJumpTo,
}: {
  step: number;
  onJumpTo: (n: number) => void;
}) {
  return (
    <div className="flex items-start overflow-x-auto no-scrollbar -mx-1 px-1">
      {STEPS.map((s, i) => {
        const state =
          s.num < step ? "done" : s.num === step ? "now" : "next";
        // Already-visited steps are clickable so the owner can jump
        // back to fix anything. Forward navigation still goes through
        // the Continue button (which validates the current step).
        const clickable = s.num < step;
        const StepShell = clickable ? "button" : "div";
        return (
          <div key={s.num} className="flex items-start flex-shrink-0">
            <StepShell
              type={clickable ? "button" : undefined}
              onClick={clickable ? () => onJumpTo(s.num) : undefined}
              aria-label={
                clickable ? `Go back to step ${s.num}: ${s.label}` : undefined
              }
              className={cx(
                "flex flex-col items-center gap-1.5 bg-transparent border-0 p-0",
                clickable &&
                  "cursor-pointer hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-white/40 rounded",
              )}
            >
              <div
                className={cx(
                  "w-[28px] h-[28px] rounded-full flex items-center justify-center text-[11px] font-semibold transition-all",
                  state === "done" && "bg-brand-400 text-brand-900",
                  state === "now" &&
                    "bg-white text-brand-800 ring-[3px] ring-white/20",
                  state === "next" &&
                    "bg-white/[0.10] text-white/30 border-[1.5px] border-white/15",
                )}
              >
                {state === "done" ? <Check size={12} /> : s.num}
              </div>
              <div
                className={cx(
                  "text-[10px] font-medium whitespace-nowrap",
                  state === "done" && "text-brand-400",
                  state === "now" && "text-white",
                  state === "next" && "text-white/30",
                )}
              >
                {s.label}
              </div>
            </StepShell>
            {i < STEPS.length - 1 && (
              <div
                className={cx(
                  "w-7 h-[1.5px] mt-3 mx-1 transition-colors",
                  state === "done" ? "bg-brand-400" : "bg-white/[0.12]",
                )}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 1 — Project type
// ---------------------------------------------------------------------------
function StepType({
  types,
  loading,
  ownsAnyUnit,
  selectedTypeIds,
  onToggle,
  onClear,
  me,
  onBehalfOfUserId,
  onPickWhiteGlove,
  onClearWhiteGlove,
}: {
  types: ProjectType[];
  loading: boolean;
  ownsAnyUnit: boolean;
  selectedTypeIds: string[];
  onToggle: (id: string) => void;
  onClear: () => void;
  me: Me | null;
  onBehalfOfUserId: string | null;
  onPickWhiteGlove: (target: { userId: string; unitId: string }) => void;
  onClearWhiteGlove: () => void;
}) {
  // Property Managers and Administrators get a "filing for an owner"
  // picker on step 1. Writing the selection back through
  // setSearchParams re-runs the unit-load effect; the rest of the
  // wizard (banner, white-glove POST forwarding) is unchanged.
  const isStaff =
    !!me &&
    me.roles.some(
      (r) => r.code === "property_manager" || r.code === "admin",
    );

  return (
    <div>
      <h2>What kind of project are you planning?</h2>
      <p className="text-[13px] text-ink-600 mt-1 mb-2 leading-relaxed">
        Pick one or more types — owners often combine work (kitchen +
        bathroom + flooring) into a single project. Required documents and
        approvals are the union of every type you select. The most-restrictive
        category drives the workflow.
      </p>

      {isStaff && !onBehalfOfUserId && (
        <WhiteGloveOwnerPicker
          me={me!}
          onPick={onPickWhiteGlove}
          ownsAnyUnit={ownsAnyUnit}
        />
      )}
      {isStaff && onBehalfOfUserId && (
        <div className="alert-info flex items-start justify-between gap-3 flex-wrap">
          <div className="text-[12.5px]">
            Filing on behalf of an owner. The owner will pick up the draft on
            their account once you save.
          </div>
          <button
            type="button"
            onClick={onClearWhiteGlove}
            className="text-[12px] text-ink-600 hover:text-ink-800 underline"
          >
            Cancel white-glove
          </button>
        </div>
      )}

      {!ownsAnyUnit && !loading && types.length > 0 && !isStaff && (
        <div className="alert-warning">
          You don't currently own any units in the building. You can browse
          types here, but you'll need a unit assignment from Property
          Management before you can save a project.
        </div>
      )}

      {selectedTypeIds.length > 0 && (
        <div className="flex items-center justify-between mb-4 mt-3 px-3 py-2 bg-brand-50 border border-brand-100 rounded-md text-[12.5px]">
          <span className="text-brand-800">
            <strong>{selectedTypeIds.length}</strong> selected — you can keep
            adding types or continue.
          </span>
          <button
            onClick={onClear}
            className="text-[12px] text-brand-700 hover:text-brand-800 underline"
          >
            Clear
          </button>
        </div>
      )}

      {loading ? (
        <p className="text-ink-500 text-sm">Loading project types…</p>
      ) : types.length === 0 ? (
        <div className="alert-info">
          No project types are configured for this building yet. An admin
          needs to seed them via{" "}
          <code>npm run db:seed</code> or add them under{" "}
          <strong>Admin → Project types</strong>.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
            {types.map((t) => {
              const selected = selectedTypeIds.includes(t.id);
              const order = selectedTypeIds.indexOf(t.id);
              return (
                <button
                  key={t.id}
                  onClick={() => onToggle(t.id)}
                  aria-pressed={selected}
                  className={cx(
                    "relative flex flex-col items-center text-center bg-white border-[1.5px] rounded-lg px-3.5 py-4 transition-all select-none",
                    "hover:-translate-y-0.5 hover:shadow-soft",
                    selected
                      ? "border-brand-600 bg-brand-50 shadow-ring"
                      : "border-sand-100 hover:border-brand-400",
                  )}
                >
                  {selected && (
                    <span className="absolute top-1.5 right-1.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-brand-600 text-white text-[10px] font-bold">
                      {order + 1}
                    </span>
                  )}
                  <span
                    className={cx(
                      "mb-2 inline-flex items-center justify-center",
                      selected ? "text-brand-700" : "text-ink-700",
                    )}
                  >
                    {TYPE_ICONS[t.code] ?? TYPE_ICON_FALLBACK}
                  </span>
                  <div
                    className={cx(
                      "text-[12px] font-medium leading-snug",
                      selected ? "text-brand-700" : "text-ink-800",
                    )}
                  >
                    {t.name}
                  </div>
                  <CatBadge cat={t.categoryCode} />
                </button>
              );
            })}
          </div>
          <BundledWorkPrompts
            types={types}
            selectedTypeIds={selectedTypeIds}
            onToggle={onToggle}
          />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bundled-work prompts
//
// When a user picks kitchen / bathroom / wall_modification, surface the
// follow-on types they almost certainly also need (per the Construction
// Rules). Each prompt is a Yes/No toggle that adds the related type to
// the selection. Avoids a class of misses where an owner submits "just
// the kitchen" and Management has to bounce it back to add flooring.
// ---------------------------------------------------------------------------
const BUNDLED_TRIGGERS: Record<string, string[]> = {
  kitchen: ["flooring_hard", "wall_modification", "washer_dryer"],
  bathroom: ["flooring_hard", "wall_modification"],
  wall_modification: ["flooring_hard"],
};
const BUNDLED_COPY: Record<
  string,
  { question: string; helper: string; cta: string }
> = {
  flooring_hard: {
    question:
      "Will this project include new hard-surface flooring (wood, tile, stone)?",
    helper:
      "Adds the three-phase Chief Engineer inspection and underlayment specs from Exhibit A/B.",
    cta: "Yes, add hard-surface flooring",
  },
  wall_modification: {
    question: "Are you removing or relocating any walls?",
    helper:
      "Adds architectural drawings and the pre-close inspection. Required for any non-load-bearing wall change.",
    cta: "Yes, add wall modification",
  },
  washer_dryer: {
    question: "Will this project add or relocate an in-unit washer/dryer?",
    helper:
      "Adds appliance specs and confirms the unit's riser still has W/D capacity.",
    cta: "Yes, add washer/dryer install",
  },
};

function BundledWorkPrompts({
  types,
  selectedTypeIds,
  onToggle,
}: {
  types: ProjectType[];
  selectedTypeIds: string[];
  onToggle: (id: string) => void;
}) {
  const selectedCodes = new Set(
    types.filter((t) => selectedTypeIds.includes(t.id)).map((t) => t.code),
  );
  const suggestionCodes = new Set<string>();
  for (const code of selectedCodes) {
    for (const target of BUNDLED_TRIGGERS[code] ?? []) {
      if (!selectedCodes.has(target)) suggestionCodes.add(target);
    }
  }
  const suggestions = Array.from(suggestionCodes)
    .map((code) => types.find((t) => t.code === code))
    .filter((t): t is ProjectType => !!t);

  if (suggestions.length === 0) return null;

  return (
    <div className="mt-7 bg-white border border-sand-100 rounded-lg p-5">
      <div className="text-[11px] font-semibold tracking-wider uppercase text-ink-400 mb-3">
        Common additions
      </div>
      <p className="text-[12.5px] text-ink-600 leading-relaxed mb-3.5">
        Owners who pick what you've selected often also include the following.
        Adding them now means a single application instead of a re-submission
        later. Same contractors, same insurance — Management treats it as one
        project.
      </p>
      <div className="flex flex-col gap-2.5">
        {suggestions.map((t) => {
          const copy = BUNDLED_COPY[t.code];
          if (!copy) return null;
          return (
            <div
              key={t.id}
              className="flex items-start gap-3 px-3.5 py-3 bg-sand-50 border border-sand-100 rounded-md"
            >
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium text-ink-800">
                  {copy.question}
                </div>
                <div className="text-[12px] text-ink-500 leading-snug mt-0.5">
                  {copy.helper}
                </div>
              </div>
              <button
                type="button"
                onClick={() => onToggle(t.id)}
                className="text-[12px] font-medium px-3 py-1.5 rounded-md bg-brand-700 text-white hover:bg-brand-800 transition-colors whitespace-nowrap"
              >
                {copy.cta}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CatBadge({ cat }: { cat: Cat }) {
  const klass = {
    A: "bg-status-approved-bg text-status-approved-fg",
    B: "bg-status-review-bg text-status-review-fg",
    C: "bg-status-rejected-bg text-status-rejected-fg",
  }[cat];
  return (
    <span
      className={cx(
        "inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold",
        klass,
      )}
      title={CAT_LABEL_LONG[cat]}
    >
      {CAT_LABEL[cat]}
      <span className="text-[9px] opacity-70 font-normal">{cat}</span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Step 2 — Project details
// ---------------------------------------------------------------------------
function StepDetails({
  units,
  unitsLoading,
  unitId,
  setUnitId,
  title,
  setTitle,
  scopeOfWork,
  setScopeOfWork,
  selectedType,
  selectedUnit,
  selectedTypes,
  guidance,
  typeDetails,
  setTypeFieldValue,
  pendingFiles,
  setPendingFiles,
  hasHardSurfaceFlooring,
  flooringDetails,
  setFlooringDetails,
}: {
  units: MyUnit[];
  unitsLoading: boolean;
  unitId: string;
  setUnitId: (s: string) => void;
  title: string;
  setTitle: (s: string) => void;
  scopeOfWork: string;
  setScopeOfWork: (s: string) => void;
  selectedType: ProjectType | null;
  selectedUnit: MyUnit | null;
  selectedTypes: ProjectType[];
  guidance: TypeGuidance | null;
  typeDetails: TypeDetails;
  setTypeFieldValue: (
    code: string,
    key: string,
    value: string | boolean,
  ) => void;
  pendingFiles: FileUpload[];
  setPendingFiles: (v: FileUpload[]) => void;
  hasHardSurfaceFlooring: boolean;
  flooringDetails: FlooringDetails;
  setFlooringDetails: (
    fn: FlooringDetails | ((prev: FlooringDetails) => FlooringDetails),
  ) => void;
}) {
  const setF = <K extends keyof FlooringDetails>(
    key: K,
    value: FlooringDetails[K],
  ) => setFlooringDetails((prev) => ({ ...prev, [key]: value }));
  const askForHelp = () => {
    const typeName = selectedType?.name ?? "construction";
    const unitNo = selectedUnit?.unitNumber ?? "[unit]";
    askElm(
      `Help me write a scope of work for a ${typeName} project at unit ${unitNo}.`,
    );
  };

  return (
    <div>
      <h2>Project details</h2>
      <p className="text-[13px] text-ink-600 mt-1 mb-6 leading-relaxed">
        Confirm your unit and describe the scope of work.
      </p>

      <SectionCard title="Location">
        {unitsLoading ? (
          <p className="text-ink-500 text-sm">Loading your units…</p>
        ) : units.length === 0 ? (
          <div className="alert-info mb-0">
            You don't have any units assigned to your account yet. Contact
            Property Management to be linked to your unit before submitting a
            project.
          </div>
        ) : (
          <Field label="Unit *">
            <select
              className="select"
              value={unitId}
              onChange={(e) => setUnitId(e.target.value)}
              required
            >
              {units.map((u) => (
                <option key={u.id} value={u.id}>
                  Unit {u.unitNumber}
                  {u.buildingName ? ` · ${u.buildingName}` : ""}
                </option>
              ))}
            </select>
          </Field>
        )}
      </SectionCard>

      <SectionCard title="Project info">
        <Field label="Title *">
          <input
            className="input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={
              guidance?.titlePlaceholder ?? "e.g. Master bath renovation"
            }
            required
            minLength={3}
          />
        </Field>

        <Field
          label={
            <span className="flex items-center justify-between">
              <span>Scope of work *</span>
              {selectedType?.scopeTemplate && (
                <span className="text-[11px] text-ink-400 normal-case tracking-normal font-normal inline-flex items-center gap-1">
                  <Sparkles size={11} className="text-brand-600" />
                  Template pre-filled — edit freely
                </span>
              )}
            </span>
          }
          hint="Name every room, fixture, and material affected. Vague scopes cause revision requests."
        >
          <textarea
            className="textarea min-h-[260px] font-mono text-[12.5px] leading-relaxed"
            value={scopeOfWork}
            onChange={(e) => setScopeOfWork(e.target.value)}
            rows={12}
            required
            minLength={10}
            placeholder="Describe the rooms, fixtures, materials, and any structural changes."
          />
        </Field>

        {guidance && guidance.scopeChecklist.length > 0 && (
          <div className="bg-sand-50 border border-sand-100 rounded-md p-3.5 mt-3">
            <div className="text-[11px] font-semibold tracking-wider uppercase text-ink-500 mb-2">
              Make sure your scope mentions
            </div>
            <ul className="text-[12.5px] text-ink-700 space-y-1.5 leading-snug list-disc pl-5">
              {guidance.scopeChecklist.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-1 mt-3">
          <button
            type="button"
            onClick={askForHelp}
            className="inline-flex items-center gap-2 px-3 py-2 text-[12.5px] text-brand-700 hover:text-brand-800 hover:bg-brand-50 rounded-md transition-colors"
          >
            <MessageCircleQuestion size={14} />
            Need help writing this? Ask Elm
          </button>
          <ScopeRefineButton
            scopeOfWork={scopeOfWork}
            projectTypeIds={selectedTypes.map((t) => t.id)}
            unitId={unitId || undefined}
            onAccept={(refined) => setScopeOfWork(refined)}
          />
        </div>
      </SectionCard>

      {selectedTypes
        .filter((t) => TYPE_EXTRA_FIELDS[t.code])
        .map((t) => (
          <ExtraFieldsCard
            key={t.id}
            typeName={t.name}
            fields={TYPE_EXTRA_FIELDS[t.code]!}
            values={typeDetails[t.code] ?? {}}
            onChange={(key, value) => setTypeFieldValue(t.code, key, value)}
            pendingFiles={pendingFiles}
            setPendingFiles={setPendingFiles}
            typeKey={t.code}
          />
        ))}

      {hasHardSurfaceFlooring && (
        <SectionCard title="Hard-surface flooring details">
          <p className="text-[12px] text-ink-500 -mt-1 mb-3 leading-relaxed">
            The Construction Rules require Management to review the specific
            floor and underlayment before any cork or plywood goes down. Add a
            link to a product page where it helps (Floor &amp; Decor, Home
            Depot, manufacturer spec sheet) — the description alone is fine
            otherwise.
          </p>

          <div className="field-row">
            <Field label="Floor type" hint="e.g. 5/8&quot; engineered oak">
              <input
                className="input"
                value={flooringDetails.floorType}
                onChange={(e) => setF("floorType", e.target.value)}
                placeholder="Engineered hardwood, oak, 5&quot; plank"
              />
            </Field>
            <Field label="Link (optional)">
              <input
                className="input"
                type="url"
                value={flooringDetails.floorTypeLink}
                onChange={(e) => setF("floorTypeLink", e.target.value)}
                placeholder="https://…"
              />
            </Field>
          </div>

          <div className="field-row">
            <Field
              label="Underlayment"
              hint='Per Exhibit B: 1/4" cork laminated to slab; 3/4" plywood for wood floors'
            >
              <input
                className="input"
                value={flooringDetails.underlayment}
                onChange={(e) => setF("underlayment", e.target.value)}
                placeholder='1/4" cork + 3/4" plywood'
              />
            </Field>
            <Field label="Link (optional)">
              <input
                className="input"
                type="url"
                value={flooringDetails.underlaymentLink}
                onChange={(e) => setF("underlaymentLink", e.target.value)}
                placeholder="https://…"
              />
            </Field>
          </div>

          <Field label="Removal of existing flooring">
            <label className="flex items-center gap-2 text-[13px] text-ink-700 cursor-pointer">
              <input
                type="checkbox"
                checked={flooringDetails.removalRequired}
                onChange={(e) => setF("removalRequired", e.target.checked)}
              />
              Existing flooring needs to be removed before installation.
            </label>
          </Field>

          {flooringDetails.removalRequired && (
            <Field
              label="Existing floor description"
              hint="What's there now? (e.g. tile over slab, glued-down hardwood, carpet + pad)"
            >
              <textarea
                className="textarea"
                rows={2}
                value={flooringDetails.currentFlooringDescription}
                onChange={(e) =>
                  setF("currentFlooringDescription", e.target.value)
                }
                placeholder="Existing 3/4&quot; oak hardwood, glued to slab, ~600 sq ft"
              />
            </Field>
          )}

          <FlooringSpecsAttach
            pendingFiles={pendingFiles}
            setPendingFiles={setPendingFiles}
          />
        </SectionCard>
      )}
    </div>
  );
}

function SectionCard({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="bg-white border border-sand-100 rounded-lg p-5 sm:p-6 mb-3.5">
      <div className="text-[11px] font-semibold tracking-wider uppercase text-ink-400 mb-3.5">
        {title}
      </div>
      {children}
    </div>
  );
}

// Flooring panel uses its own dedicated state shape (not TYPE_EXTRA_FIELDS),
// so the file picker for floor / underlayment specs is a small dedicated
// component that pushes onto pendingFiles with the
// flooring_underlayment_spec doc type.
function FlooringSpecsAttach({
  pendingFiles,
  setPendingFiles,
}: {
  pendingFiles: FileUpload[];
  setPendingFiles: (v: FileUpload[]) => void;
}) {
  const PREFIX = "type:flooring_hard:specsFile:";
  const attached = pendingFiles.filter((f) => f.id.startsWith(PREFIX));
  const remove = (id: string) =>
    setPendingFiles(pendingFiles.filter((f) => f.id !== id));
  const add = (file: File) =>
    setPendingFiles([
      ...pendingFiles,
      {
        id: `${PREFIX}${Date.now()}-${file.name}`,
        file,
        documentTypeCode: "flooring_underlayment_spec",
      },
    ]);

  return (
    <Field
      label="Specifications upload (optional)"
      hint="Manufacturer spec sheet, underlayment data sheet, installation guide, etc. Tagged as Flooring & Underlayment Specifications on Step 4."
    >
      {attached.length > 0 && (
        <ul className="flex flex-col gap-1.5 mb-2">
          {attached.map((file) => (
            <li
              key={file.id}
              className="flex items-center gap-2 px-3 py-1.5 bg-sand-50 border border-sand-100 rounded-md text-[12.5px]"
            >
              <FileText size={14} className="text-ink-500 flex-shrink-0" />
              <span className="flex-1 min-w-0 truncate text-ink-700">
                {file.file.name}
              </span>
              <span className="text-[11px] text-ink-400">
                {(file.file.size / 1024).toFixed(0)} KB
              </span>
              <button
                type="button"
                onClick={() => remove(file.id)}
                className="text-ink-500 hover:text-status-rejected-fg p-1"
                aria-label="Remove file"
              >
                <X size={12} />
              </button>
            </li>
          ))}
        </ul>
      )}
      <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-dashed border-sand-300 bg-sand-50 hover:bg-sand-100 cursor-pointer text-[12.5px] text-ink-700 transition-colors">
        <Upload size={13} />
        Choose file
        <input
          type="file"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            add(file);
            e.target.value = "";
          }}
        />
      </label>
    </Field>
  );
}

function ExtraFieldsCard({
  typeName,
  fields,
  values,
  onChange,
  pendingFiles,
  setPendingFiles,
  typeKey,
}: {
  typeName: string;
  fields: ExtraField[];
  values: Record<string, string | boolean>;
  onChange: (key: string, value: string | boolean) => void;
  pendingFiles: FileUpload[];
  setPendingFiles: (v: FileUpload[]) => void;
  /** Stable key for tagging files this panel attached to pendingFiles. */
  typeKey: string;
}) {
  // Files attached via this panel are stored with a synthetic id prefix
  // so we can reverse-lookup which type-field they came from.
  const filesByKey = (key: string) =>
    pendingFiles.filter((f) =>
      f.id.startsWith(`type:${typeKey}:${key}:`),
    );
  const removeAttachedFile = (id: string) =>
    setPendingFiles(pendingFiles.filter((f) => f.id !== id));
  const attachFile = (
    fieldKey: string,
    docTypeCode: string,
    file: File,
  ) => {
    setPendingFiles([
      ...pendingFiles,
      {
        id: `type:${typeKey}:${fieldKey}:${Date.now()}-${file.name}`,
        file,
        documentTypeCode: docTypeCode,
      },
    ]);
  };
  return (
    <SectionCard title={`${typeName} details`}>
      <p className="text-[12px] text-ink-500 -mt-1 mb-3 leading-relaxed">
        Specifics for {typeName.toLowerCase()} that Management needs to see at
        review. Fields are optional in the wizard, but submitting without them
        usually means a revision request.
      </p>
      <div className="flex flex-col gap-3">
        {fields.map((f) => {
          if (f.showWhen) {
            const cur = values[f.showWhen.key];
            if (cur !== f.showWhen.equals) return null;
          }
          if (f.kind === "notice") {
            return (
              <div
                key={f.key}
                className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[12.5px] text-ink-700"
              >
                {f.label}
                {f.hint && (
                  <span className="block text-[11.5px] text-ink-500 mt-0.5">
                    {f.hint}
                  </span>
                )}
              </div>
            );
          }
          if (f.kind === "checkbox") {
            return (
              <label
                key={f.key}
                className="flex items-start gap-2 text-[13px] text-ink-700 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={values[f.key] === true}
                  onChange={(e) => onChange(f.key, e.target.checked)}
                  className="mt-0.5"
                />
                <span>
                  {f.label}
                  {f.hint && (
                    <span className="block text-[11.5px] text-ink-500 mt-0.5">
                      {f.hint}
                    </span>
                  )}
                </span>
              </label>
            );
          }
          if (f.kind === "textarea") {
            return (
              <Field key={f.key} label={f.label} hint={f.hint}>
                <textarea
                  className="textarea"
                  rows={2}
                  value={(values[f.key] as string) ?? ""}
                  onChange={(e) => onChange(f.key, e.target.value)}
                  placeholder={f.placeholder}
                />
              </Field>
            );
          }
          if (f.kind === "file") {
            const attached = filesByKey(f.key);
            return (
              <Field key={f.key} label={f.label} hint={f.hint}>
                {attached.length > 0 && (
                  <ul className="flex flex-col gap-1.5 mb-2">
                    {attached.map((file) => (
                      <li
                        key={file.id}
                        className="flex items-center gap-2 px-3 py-1.5 bg-sand-50 border border-sand-100 rounded-md text-[12.5px]"
                      >
                        <FileText size={14} className="text-ink-500 flex-shrink-0" />
                        <span className="flex-1 min-w-0 truncate text-ink-700">
                          {file.file.name}
                        </span>
                        <span className="text-[11px] text-ink-400">
                          {(file.file.size / 1024).toFixed(0)} KB
                        </span>
                        <button
                          type="button"
                          onClick={() => removeAttachedFile(file.id)}
                          className="text-ink-500 hover:text-status-rejected-fg p-1"
                          aria-label="Remove file"
                        >
                          <X size={12} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-dashed border-sand-300 bg-sand-50 hover:bg-sand-100 cursor-pointer text-[12.5px] text-ink-700 transition-colors">
                  <Upload size={13} />
                  Choose file
                  <input
                    type="file"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      attachFile(f.key, f.documentTypeCode, file);
                      e.target.value = "";
                    }}
                  />
                </label>
              </Field>
            );
          }
          return (
            <Field key={f.key} label={f.label} hint={f.hint}>
              <input
                className="input"
                type={f.kind === "url" ? "url" : "text"}
                value={(values[f.key] as string) ?? ""}
                onChange={(e) => onChange(f.key, e.target.value)}
                placeholder={f.placeholder}
              />
            </Field>
          );
        })}
      </div>
    </SectionCard>
  );
}

// ---------------------------------------------------------------------------
// Step 3 — Impact
// ---------------------------------------------------------------------------
function StepImpact({
  requiresRiserShutdown,
  setRiser,
  requiresTelecomShutdown,
  setTelecom,
  requiresCityPermit,
  setCityPermit,
  guidance,
  selectedUnit,
}: {
  requiresRiserShutdown: boolean;
  setRiser: (b: boolean) => void;
  requiresTelecomShutdown: boolean;
  setTelecom: (b: boolean) => void;
  requiresCityPermit: boolean;
  setCityPermit: (b: boolean) => void;
  guidance: TypeGuidance | null;
  selectedUnit: MyUnit | null;
}) {
  const set = (key: ImpactKey, value: boolean) => {
    if (key === "requiresRiserShutdown") setRiser(value);
    if (key === "requiresTelecomShutdown") setTelecom(value);
    if (key === "requiresCityPermit") setCityPermit(value);
  };
  const get = (key: ImpactKey): boolean => {
    if (key === "requiresRiserShutdown") return requiresRiserShutdown;
    if (key === "requiresTelecomShutdown") return requiresTelecomShutdown;
    return requiresCityPermit;
  };

  return (
    <div>
      <h2>Building impact</h2>
      <p className="text-[13px] text-ink-600 mt-1 mb-4 leading-relaxed">
        Select every condition that applies. These flags determine which
        additional forms and fees are required at submission.
      </p>
      {guidance && guidance.impactNotes.length > 0 && (
        <div className="bg-sand-50 border border-sand-100 rounded-md p-3 mb-4 text-[12.5px] text-ink-700 leading-snug">
          <div className="text-[11px] font-semibold tracking-wider uppercase text-ink-500 mb-1.5">
            Based on what you picked
          </div>
          <ul className="list-disc pl-5 space-y-0.5">
            {guidance.impactNotes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </div>
      )}
      <div className="flex flex-col gap-2.5">
        {IMPACTS.map((imp) => {
          const selected = get(imp.key);
          return (
            <button
              key={imp.key}
              onClick={() => set(imp.key, !selected)}
              className={cx(
                "flex items-center gap-3 px-4 py-3 bg-white border-[1.5px] rounded-md text-left transition-colors",
                selected
                  ? "border-brand-500 bg-brand-50"
                  : "border-sand-100 hover:border-sand-200",
              )}
            >
              <span
                className={cx(
                  "w-5 h-5 rounded border-[1.5px] flex items-center justify-center flex-shrink-0 transition-colors",
                  selected
                    ? "bg-brand-600 border-brand-600 text-white"
                    : "bg-white border-sand-200 text-transparent",
                )}
              >
                <Check size={11} />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-[13px] font-medium text-ink-800">
                  {imp.title}
                </span>
                <span className="block text-[11.5px] text-ink-400 leading-snug">
                  {imp.desc}
                </span>
              </span>
              <span
                className={cx(
                  "text-[11px] font-semibold px-2 py-1 rounded-full flex-shrink-0",
                  selected
                    ? "bg-brand-100 text-brand-700"
                    : "bg-sand-100 text-ink-600",
                )}
              >
                {imp.feeLabel}
              </span>
            </button>
          );
        })}
      </div>
      {requiresRiserShutdown && selectedUnit && (
        <div className="mt-4">
          <RiserImpactList
            buildingId={selectedUnit.buildingId}
            riserId={selectedUnit.riserId}
            currentUnitId={selectedUnit.id}
          />
        </div>
      )}
      {requiresTelecomShutdown && (
        <div className="mt-4">
          <TelecomImpactHint />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 4 — Materials & references
// ---------------------------------------------------------------------------
function StepMaterials({
  pendingFiles,
  setPendingFiles,
  materialLinks,
  setMaterialLinks,
  guidance,
}: {
  pendingFiles: FileUpload[];
  setPendingFiles: (v: FileUpload[]) => void;
  materialLinks: MaterialLink[];
  setMaterialLinks: (v: MaterialLink[]) => void;
  guidance: TypeGuidance | null;
}) {
  const addFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPendingFiles([
      ...pendingFiles,
      {
        id: `${Date.now()}-${file.name}`,
        file,
        documentTypeCode: "scope_of_work",
      },
    ]);
    e.target.value = ""; // allow re-selecting the same file
  };
  const removeFile = (id: string) =>
    setPendingFiles(pendingFiles.filter((f) => f.id !== id));
  const updateFileType = (id: string, code: string) =>
    setPendingFiles(
      pendingFiles.map((f) =>
        f.id === id ? { ...f, documentTypeCode: code } : f,
      ),
    );

  const addLink = () =>
    setMaterialLinks([...materialLinks, { url: "", label: "" }]);
  const updateLink = (i: number, patch: Partial<MaterialLink>) =>
    setMaterialLinks(
      materialLinks.map((m, idx) => (idx === i ? { ...m, ...patch } : m)),
    );
  const removeLink = (i: number) =>
    setMaterialLinks(materialLinks.filter((_, idx) => idx !== i));

  return (
    <div>
      <h2>Materials &amp; references</h2>
      <p className="text-[13px] text-ink-600 mt-1 mb-6 leading-relaxed">
        Upload drawings, spec sheets, and "before" photos. Add links to
        materials or appliances you're considering — Home Depot, Floor &amp;
        Decor, manufacturer pages, etc.
      </p>

      <SectionCard title="Document uploads">
        <p className="text-[12px] text-ink-500 mb-3">
          PDF or image, ≤25 MB each. Files are virus-scanned on upload.
        </p>

        {pendingFiles.length > 0 && (
          <ul className="flex flex-col gap-2 mb-3">
            {pendingFiles.map((f) => (
              <li
                key={f.id}
                className="flex items-center gap-3 px-3 py-2 bg-sand-50 border border-sand-100 rounded-md"
              >
                <FileText
                  size={16}
                  className="text-ink-500 flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-[12.5px] font-medium text-ink-800 truncate">
                    {f.file.name}
                  </div>
                  <div className="text-[11px] text-ink-400">
                    {(f.file.size / 1024).toFixed(0)} KB
                  </div>
                </div>
                <select
                  className="select max-w-[220px] text-[12px] py-1 h-auto"
                  value={f.documentTypeCode}
                  onChange={(e) => updateFileType(f.id, e.target.value)}
                >
                  {DOC_TYPES_FOR_UPLOAD.map((d) => (
                    <option key={d.code} value={d.code}>
                      {d.label}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => removeFile(f.id)}
                  className="text-ink-500 hover:text-status-rejected-fg p-1"
                  aria-label="Remove file"
                >
                  <X size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}

        <label className="inline-flex items-center gap-2 px-3.5 py-2 rounded-md border border-dashed border-sand-300 bg-sand-50 hover:bg-sand-100 cursor-pointer text-[13px] text-ink-700 transition-colors">
          <Upload size={14} />
          Add file
          <input type="file" className="hidden" onChange={addFile} />
        </label>
      </SectionCard>

      <SectionCard title="Material & appliance links">
        <p className="text-[12px] text-ink-500 mb-3">
          Add as many links as you'd like — Management and the Board can click
          through to spec pages on Home Depot, Floor &amp; Decor, the
          manufacturer, etc.
        </p>

        {guidance && guidance.materialSuggestions.length > 0 && (
          <div className="bg-sand-50 border border-sand-100 rounded-md p-3 mb-3 text-[12px] text-ink-700 leading-snug">
            <div className="text-[11px] font-semibold tracking-wider uppercase text-ink-500 mb-1.5">
              For your project, owners typically link
            </div>
            <ul className="list-disc pl-5 space-y-0.5">
              {guidance.materialSuggestions.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ul>
          </div>
        )}

        {materialLinks.length === 0 ? (
          <p className="text-[12px] text-ink-400 mb-3">No links yet.</p>
        ) : (
          <ul className="flex flex-col gap-2 mb-3">
            {materialLinks.map((m, i) => (
              <li
                key={i}
                className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 items-start"
              >
                <input
                  className="input"
                  type="url"
                  value={m.url}
                  onChange={(e) => updateLink(i, { url: e.target.value })}
                  placeholder="https://homedepot.com/p/…"
                />
                <input
                  className="input"
                  value={m.label}
                  onChange={(e) => updateLink(i, { label: e.target.value })}
                  placeholder="Label (e.g. Bathroom faucet)"
                />
                <button
                  onClick={() => removeLink(i)}
                  className="px-2.5 py-2 rounded-md text-ink-500 hover:text-status-rejected-fg hover:bg-sand-50"
                  aria-label="Remove link"
                >
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}

        <button
          type="button"
          onClick={addLink}
          className="inline-flex items-center gap-1.5 text-[12.5px] text-brand-700 hover:text-brand-800 hover:bg-brand-50 px-3 py-1.5 rounded-md transition-colors"
        >
          <Plus size={14} /> Add another link
        </button>
      </SectionCard>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 5 — Contractors
// ---------------------------------------------------------------------------
function StepContractors({
  contractors,
  setContractors,
  selectedType,
  guidance,
}: {
  contractors: ContractorDraft[];
  setContractors: (v: ContractorDraft[]) => void;
  selectedType: ProjectType | null;
  guidance: TypeGuidance | null;
}) {
  const add = () =>
    setContractors([
      ...contractors,
      {
        id: `${Date.now()}-${contractors.length}`,
        discipline: contractors.length === 0 ? "general" : "plumbing",
        companyName: "",
        contactName: "",
        email: "",
        phone: "",
      },
    ]);
  const update = (id: string, patch: Partial<ContractorDraft>) =>
    setContractors(
      contractors.map((c) => (c.id === id ? { ...c, ...patch } : c)),
    );
  const remove = (id: string) =>
    setContractors(contractors.filter((c) => c.id !== id));

  return (
    <div>
      <h2>Contractor team</h2>
      <p className="text-[13px] text-ink-600 mt-1 mb-6 leading-relaxed">
        Add the companies who will work on this project. Once you save the
        draft, each contractor will receive a single-use upload link by email
        to submit their license, Certificate of Insurance, signed
        Acknowledgements, and Permission to Enter form.
      </p>

      {guidance?.contractorNote && (
        <div className="alert-info text-[12.5px]">
          {guidance.contractorNote}
        </div>
      )}

      {selectedType?.categoryCode && selectedType.categoryCode !== "A" && (
        <div className="text-[11.5px] text-ink-500 leading-snug mt-2 mb-2">
          Each contractor must carry $1M GL / $1M Auto, list the Association +
          Board + Sudler as Additional Insured, and provide 30 days cancellation
          notice.
        </div>
      )}

      {contractors.length === 0 ? (
        <p className="text-[12px] text-ink-400 mb-3">No contractors added yet.</p>
      ) : (
        <ul className="flex flex-col gap-3 mb-4">
          {contractors.map((c) => (
            <li
              key={c.id}
              className="bg-white border border-sand-100 rounded-lg p-4"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="inline-flex items-center gap-2 text-[12px] font-semibold uppercase tracking-wider text-ink-500">
                  <HardHat size={14} className="text-brand-700" />
                  {CONTRACTOR_DISCIPLINES.find((d) => d.value === c.discipline)
                    ?.label ?? c.discipline}
                </div>
                <button
                  onClick={() => remove(c.id)}
                  className="text-ink-500 hover:text-status-rejected-fg p-1"
                  aria-label="Remove contractor"
                >
                  <Trash2 size={14} />
                </button>
              </div>
              <div className="field-row">
                <Field label="Discipline">
                  <select
                    className="select"
                    value={c.discipline}
                    onChange={(e) =>
                      update(c.id, {
                        discipline: e.target
                          .value as ContractorDraft["discipline"],
                      })
                    }
                  >
                    {CONTRACTOR_DISCIPLINES.map((d) => (
                      <option key={d.value} value={d.value}>
                        {d.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Company name *">
                  <input
                    className="input"
                    value={c.companyName}
                    onChange={(e) =>
                      update(c.id, { companyName: e.target.value })
                    }
                    placeholder="Acme Plumbing LLC"
                  />
                </Field>
              </div>
              <div className="field-row">
                <Field label="Primary contact name">
                  <input
                    className="input"
                    value={c.contactName}
                    onChange={(e) =>
                      update(c.id, { contactName: e.target.value })
                    }
                    placeholder="Alex Johnson"
                  />
                </Field>
                <Field label="Primary contact email *">
                  <input
                    className="input"
                    type="email"
                    value={c.email}
                    onChange={(e) =>
                      update(c.id, { email: e.target.value })
                    }
                    placeholder="contact@acme.example"
                  />
                </Field>
                <Field label="Phone">
                  <input
                    className="input"
                    type="tel"
                    value={c.phone}
                    onChange={(e) =>
                      update(c.id, { phone: e.target.value })
                    }
                    placeholder="(555) 555-0123"
                  />
                </Field>
              </div>
            </li>
          ))}
        </ul>
      )}

      <button
        type="button"
        onClick={add}
        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md border border-dashed border-sand-300 bg-sand-50 hover:bg-sand-100 cursor-pointer text-[13px] text-ink-700 transition-colors"
      >
        <Plus size={14} /> Add contractor
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 6 — Review
// ---------------------------------------------------------------------------
function StepReview({
  types,
  unit,
  title,
  scopeOfWork,
  requiresRiserShutdown,
  requiresTelecomShutdown,
  pendingFiles,
  materialLinks,
  contractors,
}: {
  types: ProjectType[];
  unit: MyUnit | null;
  title: string;
  scopeOfWork: string;
  requiresRiserShutdown: boolean;
  requiresTelecomShutdown: boolean;
  pendingFiles: FileUpload[];
  materialLinks: MaterialLink[];
  contractors: ContractorDraft[];
}) {
  // Most-restrictive category among the selected types drives the
  // workflow on the server; mirror that here for the review summary.
  const catRank: Record<Cat, number> = { A: 1, B: 2, C: 3 };
  const sortedTypes = [...types].sort(
    (a, b) =>
      (catRank[b.categoryCode] ?? 0) - (catRank[a.categoryCode] ?? 0),
  );
  const primary = sortedTypes[0] ?? null;

  return (
    <div>
      <h2>Review &amp; confirm</h2>
      <p className="text-[13px] text-ink-600 mt-1 mb-6 leading-relaxed">
        Check your information before saving the draft. You can keep adding
        documents and contractors from the project page after saving.
      </p>

      <div className="bg-white border border-sand-100 rounded-lg overflow-hidden mb-3.5">
        <div className="px-5 sm:px-6 py-4 bg-brand-50 border-b border-brand-100 flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-brand-100 text-brand-700 flex items-center justify-center flex-shrink-0">
            {primary
              ? TYPE_ICONS[primary.code] ?? TYPE_ICON_FALLBACK
              : TYPE_ICON_FALLBACK}
          </div>
          <div>
            <div className="font-display text-[17px] font-medium text-brand-800">
              {title || "Untitled project"}
            </div>
            <div className="text-[11.5px] text-ink-400">
              Unit {unit?.unitNumber ?? "—"} ·{" "}
              {unit?.buildingName ?? "The Elm at Clark"}
            </div>
          </div>
        </div>
        <div className="px-5 sm:px-6">
          <ReviewSection title="Project details">
            <div className="sm:col-span-2">
              <div className="text-[11px] text-ink-400 mb-1">
                Project types ({types.length})
              </div>
              <div className="flex flex-wrap gap-1.5">
                {types.length === 0 && (
                  <span className="text-[12.5px] text-ink-500">—</span>
                )}
                {types.map((t, i) => (
                  <span
                    key={t.id}
                    className={cx(
                      "inline-flex items-center gap-1.5 text-[12px] px-2.5 py-1 rounded-full border",
                      i === 0
                        ? "bg-brand-50 border-brand-200 text-brand-700 font-medium"
                        : "bg-sand-50 border-sand-200 text-ink-700",
                    )}
                  >
                    {t.name}
                    <CatBadge cat={t.categoryCode} />
                  </span>
                ))}
              </div>
              {types.length > 1 && (
                <p className="text-[11.5px] text-ink-500 mt-2 leading-relaxed">
                  Documents and licenses uploaded once apply to all selected
                  types — your plumber's COI covers the kitchen and the
                  bathroom, the owner liability policy covers everything, and
                  the Indemnity Agreement is signed a single time.
                </p>
              )}
            </div>
            <ReviewField
              label="Workflow category"
              value={primary ? CAT_LABEL_LONG[primary.categoryCode] : "—"}
            />
            <ReviewField label="Unit" value={unit?.unitNumber ?? "—"} />
            <ReviewField
              label="Building"
              value={unit?.buildingName ?? "—"}
            />
            <div className="sm:col-span-2">
              <div className="text-[11px] text-ink-400 mb-0.5">
                Scope of work
              </div>
              <div className="text-[12.5px] text-ink-600 leading-relaxed bg-sand-50 border border-sand-100 rounded-sm px-3 py-2 whitespace-pre-wrap max-h-60 overflow-auto">
                {scopeOfWork || "—"}
              </div>
            </div>
          </ReviewSection>

          {(requiresRiserShutdown || requiresTelecomShutdown) && (
            <ReviewSection title="Building impact flags">
              <div className="flex flex-wrap gap-1.5 sm:col-span-2">
                {requiresRiserShutdown && (
                  <ImpactTag>Water riser shutdown (+$150)</ImpactTag>
                )}
                {requiresTelecomShutdown && (
                  <ImpactTag>Telecom shutdown</ImpactTag>
                )}
              </div>
            </ReviewSection>
          )}

          {pendingFiles.length > 0 && (
            <ReviewSection title={`Documents (${pendingFiles.length})`}>
              <ul className="sm:col-span-2 flex flex-col gap-1">
                {pendingFiles.map((f) => (
                  <li
                    key={f.id}
                    className="flex items-center gap-2 text-[12.5px]"
                  >
                    <FileText size={13} className="text-ink-500" />
                    <span className="text-ink-800">{f.file.name}</span>
                    <span className="text-ink-400">
                      ·{" "}
                      {DOC_TYPES_FOR_UPLOAD.find(
                        (d) => d.code === f.documentTypeCode,
                      )?.label ?? f.documentTypeCode}
                    </span>
                  </li>
                ))}
              </ul>
            </ReviewSection>
          )}

          {materialLinks.length > 0 && (
            <ReviewSection title={`Material links (${materialLinks.length})`}>
              <ul className="sm:col-span-2 flex flex-col gap-1">
                {materialLinks.map((m, i) => (
                  <li
                    key={i}
                    className="flex items-center gap-2 text-[12.5px]"
                  >
                    <ExternalLink size={13} className="text-ink-500" />
                    <span className="text-ink-800">
                      {m.label || m.url || "—"}
                    </span>
                    {m.label && m.url && (
                      <span className="text-ink-400 truncate">· {m.url}</span>
                    )}
                  </li>
                ))}
              </ul>
            </ReviewSection>
          )}

          {contractors.length > 0 && (
            <ReviewSection title={`Contractors (${contractors.length})`}>
              <ul className="sm:col-span-2 flex flex-col gap-1">
                {contractors.map((c) => (
                  <li key={c.id} className="text-[12.5px]">
                    <span className="text-ink-800 font-medium">
                      {c.companyName || "(unnamed)"}
                    </span>
                    <span className="text-ink-400">
                      {" "}
                      · {c.discipline}
                      {c.contactName ? ` · ${c.contactName}` : ""}
                      {" · "}
                      {c.email || "—"}
                    </span>
                  </li>
                ))}
              </ul>
            </ReviewSection>
          )}
        </div>
      </div>

      <p className="text-[11.5px] text-ink-400 px-1 leading-relaxed">
        Saving creates a <strong>draft</strong>. The portal will email each
        listed contractor a single-use upload link. You can submit the
        project for management review from the project page once the
        documents and acknowledgements are in place.
      </p>
    </div>
  );
}

function ReviewSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="py-4 border-b last:border-b-0 border-sand-100">
      <div className="text-[10.5px] font-semibold tracking-wider uppercase text-ink-400 mb-2.5">
        {title}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2.5 gap-x-6">
        {children}
      </div>
    </div>
  );
}

function ReviewField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[11px] text-ink-400 mb-0.5">{label}</div>
      <div className="text-[13px] font-medium text-ink-800">{value}</div>
    </div>
  );
}

function ImpactTag({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[11.5px] px-2.5 py-1 bg-brand-50 border border-brand-100 rounded-full text-brand-700">
      <Check size={11} /> {children}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Sidebar variants
// ---------------------------------------------------------------------------
function SidebarHeader({ children }: { children: ReactNode }) {
  return (
    <div className="text-[10.5px] font-semibold tracking-wider uppercase text-ink-400 mb-3.5">
      {children}
    </div>
  );
}

function Tip({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <div className="flex gap-2.5 items-start">
      <div className="w-[26px] h-[26px] rounded-md bg-brand-50 border border-brand-100 flex items-center justify-center flex-shrink-0 mt-px text-brand-700">
        {icon}
      </div>
      <p className="text-[12px] text-ink-600 leading-relaxed">{children}</p>
    </div>
  );
}

function SidebarCategories() {
  return (
    <>
      <SidebarHeader>About project categories</SidebarHeader>
      <div className="flex flex-col gap-3">
        <Tip icon={<Info size={13} />}>
          <strong className="text-ink-800 font-medium">
            Decorative <span className="text-ink-400 font-normal">(A)</span>
          </strong>{" "}
          — paint, wallpaper, carpet. Management notification only; no
          Board approval, no deposit.
        </Tip>
        <Tip icon={<FileText size={13} />}>
          <strong className="text-ink-800 font-medium">
            System-affecting <span className="text-ink-400 font-normal">(B)</span>
          </strong>{" "}
          — kitchen, bath, washer/dryer, hard floors. Board approval (up to
          3 weeks), $500 deposit, phased inspections.
        </Tip>
        <Tip icon={<Shield size={13} />}>
          <strong className="text-ink-800 font-medium">
            Structural <span className="text-ink-400 font-normal">(C)</span>
          </strong>{" "}
          additionally requires a signed License Agreement with the
          Association.
        </Tip>
      </div>
    </>
  );
}

function SidebarSelection({ type }: { type: ProjectType | null }) {
  return (
    <>
      <SidebarHeader>Your selection</SidebarHeader>
      <div className="flex justify-between items-start py-2 border-b border-sand-100 gap-2.5">
        <div className="text-[11.5px] text-ink-400">Type</div>
        <div className="text-[12px] font-semibold text-brand-700 text-right max-w-[55%]">
          {type?.name ?? "—"}
        </div>
      </div>
      <div className="flex justify-between items-start py-2 gap-2.5">
        <div className="text-[11.5px] text-ink-400">Category</div>
        <div className="text-[12px] font-medium text-ink-800 text-right max-w-[55%]">
          {type ? CAT_LABEL_LONG[type.categoryCode] : "—"}
        </div>
      </div>
      <div className="mt-6">
        <SidebarHeader>Writing a good scope</SidebarHeader>
        <div className="flex flex-col gap-3">
          <Tip icon={<PencilLine size={13} />}>
            Name every room, fixture, and material affected. Vague scopes
            cause revision requests.
          </Tip>
          <Tip icon={<Clock size={13} />}>
            Mention if any walls are being removed, moved, or opened — this
            determines the Chief Engineer's inspection schedule.
          </Tip>
        </div>
      </div>
    </>
  );
}

function SidebarFees({
  feeTotal,
  requiresRiserShutdown,
}: {
  feeTotal: number;
  requiresRiserShutdown: boolean;
}) {
  return (
    <>
      <SidebarHeader>Fee estimate</SidebarHeader>
      <div className="bg-brand-50 border border-brand-100 rounded-md px-4 py-3.5">
        <div className="flex justify-between text-[12px] mb-1.5">
          <span className="text-ink-600">Application fee</span>
          <span className="text-ink-800 font-medium">$0</span>
        </div>
        {requiresRiserShutdown && (
          <div className="flex justify-between text-[12px] mb-1.5">
            <span className="text-ink-600">Riser shutdown</span>
            <span className="text-ink-800 font-medium">$150</span>
          </div>
        )}
        <div className="flex justify-between pt-2 mt-1 border-t border-brand-200 text-[13px] font-semibold">
          <span className="text-ink-700">Estimated total</span>
          <span className="text-brand-700">${feeTotal}</span>
        </div>
      </div>
      <p className="text-[11px] text-ink-400 mt-2.5 leading-relaxed">
        Security deposit ($500) and architectural fee are collected at
        submission, not at draft creation.
      </p>
    </>
  );
}

function SidebarMaterials() {
  return (
    <>
      <SidebarHeader>What to upload</SidebarHeader>
      <div className="flex flex-col gap-3">
        <Tip icon={<FileText size={13} />}>
          <strong className="text-ink-800 font-medium">
            Drawings &amp; specs
          </strong>{" "}
          for any structural change, plumbing relocation, or hard-flooring
          install.
        </Tip>
        <Tip icon={<WashingMachine size={13} />}>
          For washer/dryer installs, attach the appliance spec sheet and the{" "}
          <strong className="text-ink-800 font-medium">
            ventless dryer
          </strong>{" "}
          and leak-sensor specifications per the Rules.
        </Tip>
        <Tip icon={<LinkIcon size={13} />}>
          Add a link for every fixture, finish, or appliance you're
          considering. Reviewers click through to see the spec page.
        </Tip>
      </div>
    </>
  );
}

function SidebarContractors() {
  return (
    <>
      <SidebarHeader>Contractor requirements</SidebarHeader>
      <div className="flex flex-col gap-3">
        <Tip icon={<HardHat size={13} />}>
          <strong className="text-ink-800 font-medium">
            Plumbing &amp; electrical
          </strong>{" "}
          must be performed by a City of Chicago licensed plumber /
          electrician.
        </Tip>
        <Tip icon={<Shield size={13} />}>
          Each contractor needs $1M General Liability, $1M Auto, statutory
          Workers Comp, and must list the Association + Board + Sudler as{" "}
          <strong className="text-ink-800 font-medium">
            Additional Insured
          </strong>
          .
        </Tip>
        <Tip icon={<ChevronRight size={13} />}>
          On save, each contractor receives a single-use email link to upload
          their license, COI, signed Acknowledgements, and Permission to
          Enter form.
        </Tip>
      </div>
    </>
  );
}

function SidebarAfterSave() {
  return (
    <>
      <SidebarHeader>What happens after saving</SidebarHeader>
      <div className="flex flex-col gap-3">
        <Tip icon={<Save size={13} />}>
          <strong className="text-ink-800 font-medium">Draft saved</strong> —
          continue uploading documents and adding contractors from the
          project page.
        </Tip>
        <Tip icon={<Shield size={13} />}>
          <strong className="text-ink-800 font-medium">
            Management review
          </strong>{" "}
          checks completeness before it reaches the Board.
        </Tip>
        <Tip icon={<Megaphone size={13} />}>
          <strong className="text-ink-800 font-medium">
            Track progress
          </strong>{" "}
          in your Projects dashboard with real-time status updates.
        </Tip>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// White-glove owner picker.
//
// Renders one row per (owner, unit) pair across the buildings the
// principal manages. PM/admin only — gated at the call site, which
// also handles the "already in white-glove mode" path. Selection
// writes the userId + unitId into the URL search params so the
// wizard's existing white-glove path picks it up.
//
// We don't auto-pre-select; the staff member should consciously
// confirm who they're filing for. A hint at the bottom points at
// AdminUsers as the alternative entry point so the same wizard
// can also be launched from there.
// ---------------------------------------------------------------------------
type WhiteGloveOwner = {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  unitId: string;
  unitNumber: string;
  floor: number | null;
};

function WhiteGloveOwnerPicker({
  me,
  ownsAnyUnit,
  onPick,
}: {
  me: Me;
  ownsAnyUnit: boolean;
  onPick: (target: { userId: string; unitId: string }) => void;
}) {
  // Buildings the principal can white-glove for. Deduped because a
  // user can hold both PM and admin in the same building.
  const buildings = useMemo(() => {
    const seen = new Map<string, string>();
    for (const r of me.roles) {
      if (r.code !== "property_manager" && r.code !== "admin") continue;
      if (r.buildingName && !seen.has(r.buildingId)) {
        seen.set(r.buildingId, r.buildingName);
      }
    }
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
  }, [me.roles]);

  const [buildingId, setBuildingId] = useState(buildings[0]?.id ?? "");
  const [owners, setOwners] = useState<WhiteGloveOwner[] | null>(null);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    if (!buildingId) {
      setOwners([]);
      return;
    }
    setOwners(null);
    void api
      .get<{ data: WhiteGloveOwner[] }>(
        `/api/management/buildings/${buildingId}/owners`,
      )
      .then((r) => setOwners(r.data))
      .catch(() => setOwners([]));
  }, [buildingId]);

  const filtered = useMemo(() => {
    if (!owners) return [];
    const q = filter.trim().toLowerCase();
    if (!q) return owners;
    return owners.filter((o) =>
      [
        `${o.firstName} ${o.lastName}`,
        o.email,
        `unit ${o.unitNumber}`,
      ]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [owners, filter]);

  if (buildings.length === 0) return null;

  return (
    <div className="rounded-md border border-brand-100 bg-brand-50/40 p-3.5 mb-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[12.5px] font-semibold text-brand-800 mb-0.5">
            Filing for an owner?
          </div>
          <p className="text-[12px] text-ink-600 leading-snug m-0">
            Pick the owner whose unit this project is for. The wizard will
            switch to white-glove mode and the owner picks up the draft on
            their account once you save.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-2 mt-3">
        {buildings.length > 1 && (
          <select
            className="select"
            value={buildingId}
            onChange={(e) => setBuildingId(e.target.value)}
          >
            {buildings.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        )}
        <input
          className="input"
          placeholder="Filter by name, email, or unit"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      </div>

      <div className="mt-3 max-h-64 overflow-y-auto rounded-md border border-brand-100 bg-white">
        {owners === null ? (
          <p className="text-[12px] text-ink-500 px-3 py-3 m-0">
            Loading owners…
          </p>
        ) : filtered.length === 0 ? (
          <p className="text-[12px] text-ink-500 px-3 py-3 m-0">
            {owners.length === 0
              ? "No active owners in this building yet."
              : "No matches for that filter."}
          </p>
        ) : (
          <ul className="divide-y divide-sand-100 m-0 p-0 list-none">
            {filtered.map((o) => (
              <li
                key={`${o.userId}:${o.unitId}`}
                className="flex items-center justify-between gap-3 px-3 py-2.5"
              >
                <div className="min-w-0">
                  <div className="text-[13px] font-medium text-ink-800 truncate">
                    {o.firstName} {o.lastName}
                  </div>
                  <div className="text-[11.5px] text-ink-500 truncate">
                    Unit {o.unitNumber}
                    {o.floor != null ? ` · Floor ${o.floor}` : ""} ·{" "}
                    {o.email}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    onPick({ userId: o.userId, unitId: o.unitId })
                  }
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[12px] text-brand-700 hover:text-brand-800 hover:bg-brand-50 whitespace-nowrap"
                >
                  Use this unit
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {!ownsAnyUnit && (
        <p className="text-[11px] text-ink-500 mt-2.5 m-0 leading-snug">
          You can also start a white-glove submission from any owner's profile
          on the Admin · Users page — the wizard opens with the owner +
          unit pre-filled.
        </p>
      )}
    </div>
  );
}
